import React, { useEffect, useState } from 'react';
import styles from './AdminShared.module.scss';
import { AdminSiteConfigurationUpdateDto } from '../../../types/admin.types';
import { fetchSiteConfiguration, updateSiteConfiguration } from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';
import AdminDateTimeInput from './components/AdminDateTimeInput';
import { toDateInput, toUtcDateString } from '../../../utils/date-time';

type SiteConfigurationForm = AdminSiteConfigurationUpdateDto & {
    HasMetaConversionsApiAccessToken?: boolean;
};

const AdminSiteConfiguration = () => {
    const { getUserInfo } = useUserState();
    const userInfo = getUserInfo();
    const token = userInfo?.token;
    const [form, setForm] = useState<SiteConfigurationForm | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const loadConfig = async () => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const response = await fetchSiteConfiguration(token);
            setForm({
                Id: response.Id,
                ContactName: response.ContactName ?? '',
                ContactEmail: response.ContactEmail ?? '',
                ContactPhone: response.ContactPhone ?? '',
                Address: response.Address ?? '',
                DefaultVideoPrice: response.DefaultVideoPrice ?? null,
                DefaultVideoAccessUntil: toDateInput(response.DefaultVideoAccessUntil),
                EnableMarketingTags: response.EnableMarketingTags ?? false,
                RequireMarketingConsent: response.RequireMarketingConsent ?? true,
                EnableGoogleTagManager: response.EnableGoogleTagManager ?? false,
                GoogleTagManagerContainerId: response.GoogleTagManagerContainerId ?? '',
                GoogleAnalyticsMeasurementId: response.GoogleAnalyticsMeasurementId ?? '',
                GoogleAdsConversionId: response.GoogleAdsConversionId ?? '',
                GoogleAdsPageViewConversionLabel: response.GoogleAdsPageViewConversionLabel ?? '',
                GoogleAdsSignupConversionLabel: response.GoogleAdsSignupConversionLabel ?? '',
                GoogleAdsSubscribeConversionLabel: response.GoogleAdsSubscribeConversionLabel ?? '',
                GoogleAdsPurchaseConversionLabel: response.GoogleAdsPurchaseConversionLabel ?? '',
                EnableMetaPixel: response.EnableMetaPixel ?? false,
                MetaPixelId: response.MetaPixelId ?? '',
                EnableMetaConversionsApi: response.EnableMetaConversionsApi ?? false,
                HasMetaConversionsApiAccessToken: response.HasMetaConversionsApiAccessToken ?? false,
                MetaConversionsApiAccessToken: '',
                ClearMetaConversionsApiAccessToken: false
            });
        } catch (error) {
            setErrorMessage('Unable to load site configuration.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadConfig();
    }, []);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!form) {
            return;
        }
        setErrorMessage(null);
        setSuccessMessage(null);
        try {
            if (form.DefaultVideoPrice == null || Number.isNaN(form.DefaultVideoPrice)) {
                setErrorMessage('Default video price is required.');
                return;
            }

            const accessUntil = form.DefaultVideoAccessUntil || '2099-12-31';
            const updateForm: SiteConfigurationForm = { ...form };
            delete updateForm.HasMetaConversionsApiAccessToken;
            const payload: AdminSiteConfigurationUpdateDto = {
                ...updateForm,
                DefaultVideoAccessUntil: toUtcDateString(accessUntil)
            };

            await updateSiteConfiguration(payload, token);
            setSuccessMessage('Site configuration saved.');
        } catch (error) {
            setErrorMessage('Unable to save site configuration.');
        }
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Site Configuration</h1>
                    <p>Manage contact, pricing, and public marketing measurement settings.</p>
                </div>
            </header>

            {errorMessage ? <div className={styles.muted}>{errorMessage}</div> : null}
            {successMessage ? <div className={styles.muted}>{successMessage}</div> : null}

            <section className={styles.panel}>
                {isLoading ? (
                    <div className={styles.muted}>Loading configuration...</div>
                ) : form ? (
                    <form onSubmit={handleSubmit}>
                        <h2>Contact and Pricing</h2>
                        <div className={styles.field}>
                            <label htmlFor="site-contact-name">Contact Name</label>
                            <input
                                id="site-contact-name"
                                value={form.ContactName ?? ''}
                                onChange={(event) => setForm({ ...form, ContactName: event.target.value })}
                                maxLength={255}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-contact-email">Contact Email</label>
                            <input
                                id="site-contact-email"
                                type="email"
                                value={form.ContactEmail ?? ''}
                                onChange={(event) => setForm({ ...form, ContactEmail: event.target.value })}
                                maxLength={255}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-contact-phone">Contact Phone</label>
                            <input
                                id="site-contact-phone"
                                value={form.ContactPhone ?? ''}
                                onChange={(event) => setForm({ ...form, ContactPhone: event.target.value })}
                                maxLength={50}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-contact-address">Address</label>
                            <textarea
                                id="site-contact-address"
                                value={form.Address ?? ''}
                                onChange={(event) => setForm({ ...form, Address: event.target.value })}
                                maxLength={2000}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-video-price">Default Video Price (NZD)</label>
                            <input
                                id="site-video-price"
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.DefaultVideoPrice ?? ''}
                                onChange={(event) => setForm({ ...form, DefaultVideoPrice: event.target.value === '' ? null : Number(event.target.value) })}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-video-access-until">Default Video Access Until</label>
                            <AdminDateTimeInput
                                id="site-video-access-until"
                                value={form.DefaultVideoAccessUntil ?? ''}
                                onChange={(value) => setForm({ ...form, DefaultVideoAccessUntil: value })}
                            />
                        </div>
                        <h2>Advertising Measurement</h2>
                        <p className={styles.muted}>
                            Store Google and Meta advertising settings here. Private Meta CAPI tokens are write-only:
                            leave the token blank to keep the current saved value.
                        </p>
                        <div className={styles.field}>
                            <label htmlFor="site-enable-marketing">
                                <input
                                    id="site-enable-marketing"
                                    type="checkbox"
                                    checked={form.EnableMarketingTags ?? false}
                                    onChange={(event) => setForm({ ...form, EnableMarketingTags: event.target.checked })}
                                />
                                Enable marketing tags
                            </label>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-require-marketing-consent">
                                <input
                                    id="site-require-marketing-consent"
                                    type="checkbox"
                                    checked={form.RequireMarketingConsent ?? true}
                                    onChange={(event) => setForm({ ...form, RequireMarketingConsent: event.target.checked })}
                                />
                                Require visitor consent before loading tags
                            </label>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-enable-gtm">
                                <input
                                    id="site-enable-gtm"
                                    type="checkbox"
                                    checked={form.EnableGoogleTagManager ?? false}
                                    onChange={(event) => setForm({ ...form, EnableGoogleTagManager: event.target.checked })}
                                />
                                Enable Google Tag Manager
                            </label>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-gtm-id">Google Tag Manager Container ID</label>
                            <input
                                id="site-gtm-id"
                                value={form.GoogleTagManagerContainerId ?? ''}
                                onChange={(event) => setForm({ ...form, GoogleTagManagerContainerId: event.target.value })}
                                maxLength={100}
                                placeholder="GTM-XXXXXXX"
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-ga-id">Google Analytics Measurement ID</label>
                            <input
                                id="site-ga-id"
                                value={form.GoogleAnalyticsMeasurementId ?? ''}
                                onChange={(event) => setForm({ ...form, GoogleAnalyticsMeasurementId: event.target.value })}
                                maxLength={100}
                                placeholder="G-XXXXXXXXXX"
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-google-ads-id">Google Ads Conversion ID</label>
                            <input
                                id="site-google-ads-id"
                                value={form.GoogleAdsConversionId ?? ''}
                                onChange={(event) => setForm({ ...form, GoogleAdsConversionId: event.target.value })}
                                maxLength={100}
                                placeholder="AW-XXXXXXXXX"
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-google-page-view-label">Google Ads Page View Label</label>
                            <input
                                id="site-google-page-view-label"
                                value={form.GoogleAdsPageViewConversionLabel ?? ''}
                                onChange={(event) => setForm({ ...form, GoogleAdsPageViewConversionLabel: event.target.value })}
                                maxLength={200}
                            />
                            <p className={styles.muted}>Paste the Google-generated label for landing page or page-view conversions.</p>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-google-signup-label">Google Ads Signup Label</label>
                            <input
                                id="site-google-signup-label"
                                value={form.GoogleAdsSignupConversionLabel ?? ''}
                                onChange={(event) => setForm({ ...form, GoogleAdsSignupConversionLabel: event.target.value })}
                                maxLength={200}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-google-subscribe-label">Google Ads Subscribe Label</label>
                            <input
                                id="site-google-subscribe-label"
                                value={form.GoogleAdsSubscribeConversionLabel ?? ''}
                                onChange={(event) => setForm({ ...form, GoogleAdsSubscribeConversionLabel: event.target.value })}
                                maxLength={200}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-google-purchase-label">Google Ads Purchase Label</label>
                            <input
                                id="site-google-purchase-label"
                                value={form.GoogleAdsPurchaseConversionLabel ?? ''}
                                onChange={(event) => setForm({ ...form, GoogleAdsPurchaseConversionLabel: event.target.value })}
                                maxLength={200}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-enable-meta-pixel">
                                <input
                                    id="site-enable-meta-pixel"
                                    type="checkbox"
                                    checked={form.EnableMetaPixel ?? false}
                                    onChange={(event) => setForm({ ...form, EnableMetaPixel: event.target.checked })}
                                />
                                Enable Meta Pixel
                            </label>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-meta-pixel-id">Meta Pixel ID</label>
                            <input
                                id="site-meta-pixel-id"
                                value={form.MetaPixelId ?? ''}
                                onChange={(event) => setForm({ ...form, MetaPixelId: event.target.value })}
                                maxLength={100}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-enable-meta-capi">
                                <input
                                    id="site-enable-meta-capi"
                                    type="checkbox"
                                    checked={form.EnableMetaConversionsApi ?? false}
                                    onChange={(event) => setForm({ ...form, EnableMetaConversionsApi: event.target.checked })}
                                />
                                Queue Meta Conversions API server events
                            </label>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-meta-capi-token">Meta Conversions API Access Token</label>
                            <input
                                id="site-meta-capi-token"
                                type="password"
                                value={form.MetaConversionsApiAccessToken ?? ''}
                                onChange={(event) => setForm({
                                    ...form,
                                    MetaConversionsApiAccessToken: event.target.value,
                                    ClearMetaConversionsApiAccessToken: false
                                })}
                                maxLength={1024}
                                placeholder={form.HasMetaConversionsApiAccessToken
                                    ? 'Configured - leave blank to keep current token'
                                    : 'Paste Meta CAPI access token'}
                                disabled={form.ClearMetaConversionsApiAccessToken ?? false}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="site-clear-meta-capi-token">
                                <input
                                    id="site-clear-meta-capi-token"
                                    type="checkbox"
                                    checked={form.ClearMetaConversionsApiAccessToken ?? false}
                                    onChange={(event) => setForm({
                                        ...form,
                                        ClearMetaConversionsApiAccessToken: event.target.checked,
                                        MetaConversionsApiAccessToken: event.target.checked ? '' : form.MetaConversionsApiAccessToken
                                    })}
                                />
                                Clear saved Meta CAPI token
                            </label>
                        </div>
                        <div className={styles.actions}>
                            <button className={styles.primary} type="submit">Save</button>
                        </div>
                    </form>
                ) : (
                    <div className={styles.empty}>No configuration available.</div>
                )}
            </section>
        </div>
    );
};

export default AdminSiteConfiguration;
